import React, { useState } from 'react';
import { MONTH_NAMES } from '../utils/constants';
import SearchableSelect from './SearchableSelect';

// The payroll team's running notes on a client's month ("waiting for Jane to
// confirm the bonus"). Presentational: the parent loads the notes and passes the
// add / toggle / delete handlers, so the dashboard (all clients) and the
// processing view (one client) share it.
//
// `companies` given → shows a client picker and the client name on each note.
// `companyId` given instead → notes are added against that client.
export default function PayrollNotes({
  notes, month, year, companies, companyId, canDelete, onAdd, onToggle, onDelete, compact = false,
}) {
  const [text, setText] = useState('');
  const [pickedCompany, setPickedCompany] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showDone, setShowDone] = useState(false);

  const targetCompany = companyId || pickedCompany;
  const open = notes.filter(n => !n.done);
  const done = notes.filter(n => n.done);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    if (!targetCompany) { setError('Choose a client'); return; }
    setSaving(true);
    setError('');
    try {
      await onAdd({ company_id: targetCompany, note: text.trim() });
      setText('');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  const earlierMonth = (n) => n.calendar_month !== month || n.calendar_year !== year;

  const renderNote = (n) => (
    <li key={n.id} className="flex items-start gap-3 py-2.5">
      <input
        type="checkbox"
        checked={n.done}
        onChange={() => onToggle(n)}
        aria-label={n.done ? 'Mark as not done' : 'Mark as done'}
        className="mt-1 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
      />
      <div className="min-w-0 flex-1">
        {companies && <p className="text-[14px] font-semibold text-ink">{n.company_name}</p>}
        <p className={`text-[15px] whitespace-pre-wrap break-words ${n.done ? 'text-gray-400 line-through' : 'text-gray-800'}`}>{n.note}</p>
        <p className="text-[12.5px] text-gray-500 mt-0.5">
          {n.created_by_name || 'Unknown'} · {new Date(n.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
          {earlierMonth(n) && !n.done && (
            <span className="ml-2 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-medium">
              from {MONTH_NAMES[n.calendar_month - 1].slice(0, 3)} {n.calendar_year}
            </span>
          )}
          {n.done && n.done_by_name && <> · done by {n.done_by_name}</>}
        </p>
      </div>
      {canDelete(n) && (
        <button
          onClick={() => onDelete(n)}
          className="text-[13px] text-gray-400 hover:text-rose-600 shrink-0"
          aria-label="Delete note"
        >
          Delete
        </button>
      )}
    </li>
  );

  return (
    <div>
      <form onSubmit={submit} className="space-y-2">
        {companies && (
          <SearchableSelect
            value={pickedCompany}
            onChange={setPickedCompany}
            placeholder="Choose client…"
            options={companies.map(c => ({ value: c.id, label: c.company_name }))}
          />
        )}
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          rows={compact ? 2 : 3}
          maxLength={2000}
          placeholder="e.g. Waiting for Jane to confirm the bonus amount"
          className="block w-full rounded-md border-gray-300 shadow-sm text-[14.5px] px-3 py-2 focus:ring-brand-500 focus:border-brand-500"
        />
        <div className="flex items-center justify-between gap-2">
          {error ? <p className="text-[13px] text-rose-600">{error}</p> : <span />}
          <button
            type="submit"
            disabled={saving || !text.trim()}
            className="px-3 py-1.5 text-[14px] font-medium rounded-md bg-brand-600 hover:bg-brand-700 text-white disabled:opacity-50"
          >
            {saving ? 'Adding…' : 'Add note'}
          </button>
        </div>
      </form>

      {open.length === 0 && done.length === 0 ? (
        <p className="text-[14px] text-gray-500 mt-3">No notes for {MONTH_NAMES[month - 1]} yet.</p>
      ) : (
        <>
          {open.length > 0 && <ul className="divide-y divide-gray-100 mt-2">{open.map(renderNote)}</ul>}
          {open.length === 0 && <p className="text-[14px] text-gray-500 mt-3">Nothing outstanding.</p>}
          {done.length > 0 && (
            <div className="mt-2">
              <button onClick={() => setShowDone(v => !v)} className="text-[13.5px] text-gray-500 hover:text-gray-800">
                {showDone ? 'Hide' : 'Show'} {done.length} done
              </button>
              {showDone && <ul className="divide-y divide-gray-100">{done.map(renderNote)}</ul>}
            </div>
          )}
        </>
      )}
    </div>
  );
}
